import { Divider } from '@affine/component';
import { IconButton } from '@affine/component/ui/button';
import { downloadFile } from '@affine/core/utils/resource';
import {
  ArrowLeftSmallIcon,
  ArrowRightSmallIcon,
  CloseIcon,
  DownloadIcon,
} from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback, useState } from 'react';

import { PeekViewService } from '../../services/peek-view';
import * as styles from './index.css';

export type MediaPreviewKind = 'audio' | 'video';

export interface MediaData {
  index?: number;
  url: string;
  caption?: string;
  mime?: string;
  kind: MediaPreviewKind;
  previous?: () => MediaData | undefined;
  next?: () => MediaData | undefined;
}

export interface MediaPreviewData {
  media: MediaData;
  total?: number;
}

export interface MediaPreviewProps extends MediaPreviewData {
  onClose: () => void;
}

export const GenericMediaPreviewModal = ({
  media,
  total,
  onClose,
}: MediaPreviewProps) => {
  const downloadHandler = useCallback(() => {
    if (!media.url) return;
    downloadFile(media.url, media.caption || 'media');
  }, [media.caption, media.url]);

  return (
    <div className={styles.mediaPreviewModal}>
      <div className={styles.mediaPreviewTrap} onClick={onClose} />
      <div className={styles.mediaPreviewContainer}>
        {media.kind === 'video' ? (
          <video
            key={media.url}
            className={styles.mediaPreviewVideo}
            controls
            src={media.url}
          />
        ) : (
          <audio
            key={media.url}
            className={styles.mediaPreviewAudio}
            controls
            src={media.url}
          />
        )}
        {media.caption ? (
          <p className={styles.mediaPreviewCaption}>{media.caption}</p>
        ) : null}
      </div>

      <div className={styles.mediaPreviewActionBar}>
        <IconButton
          data-testid="previous-media-button"
          tooltip="Previous"
          icon={<ArrowLeftSmallIcon />}
          disabled={!media.previous}
          onClick={media.previous}
        />
        {media.index != null && total != null && (
          <div className={styles.mediaPreviewCursor}>
            {`${media.index + 1}/${total}`}
          </div>
        )}
        <IconButton
          data-testid="next-media-button"
          tooltip="Next"
          icon={<ArrowRightSmallIcon />}
          disabled={!media.next}
          onClick={media.next}
        />
        <Divider size="thinner" orientation="vertical" />
        <IconButton
          data-testid="download-media-button"
          tooltip="Download"
          icon={<DownloadIcon />}
          onClick={downloadHandler}
        />
      </div>
    </div>
  );
};

export const GenericMediaPreviewModalWithClose = (
  props: Omit<MediaPreviewProps, 'onClose'>
) => {
  const peekViewService = useService(PeekViewService);
  const handleClose = useCallback(() => {
    peekViewService.peekView.close();
  }, [peekViewService]);

  const [media, setMedia] = useState<MediaData>(props.media);

  const prevMedia = useCallback(() => {
    const prev = media.previous?.();
    if (!prev) return;
    setMedia(prev);
    return prev;
  }, [media]);

  const nextMedia = useCallback(() => {
    const next = media.next?.();
    if (!next) return;
    setMedia(next);
    return next;
  }, [media]);

  return (
    <>
      <GenericMediaPreviewModal
        total={props.total}
        media={{
          index: media.index,
          url: media.url,
          caption: media.caption,
          mime: media.mime,
          kind: media.kind,
          previous: prevMedia,
          next: nextMedia,
        }}
        onClose={handleClose}
      />
      <button
        data-testid="media-preview-close-button"
        onClick={handleClose}
        className={styles.mediaPreviewCloseButton}
      >
        <CloseIcon />
      </button>
    </>
  );
};
